/**
 * 🔐 COMPONENTE DE ROTA PROTEGIDA MELHORADO
 * 
 * Este componente verifica autenticação de forma robusta, considerando:
 * - Estado de loading da autenticação
 * - Tokens no localStorage
 * - Dados do usuário no Redux
 * - Timeouts para carregamento
 */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { RootState } from '../../redux/store';
import { isAuthenticated } from '../../Backend/services/authService';
import padelLogo from '../../assets/images/PadelMaster v1.png';
import tennisBall from '../../assets/images/tennis-ball.png';

// Ecrã de verificação profissional — navbar + fundo do court (como a home) + logo + spinner
const VerifyingScreen: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <div className="fixed inset-0 z-[100] flex flex-col">
    {/* Barra tipo navbar */}
    <header className="flex shrink-0 items-center gap-3 border-b border-emerald-100 bg-[#e8f3ea] px-5 py-3">
      <img src={tennisBall} alt="" className="h-8 w-8 rounded-full object-contain" />
      <span className="text-lg font-bold tracking-tight text-gray-900">PadelMaster</span>
    </header>

    {/* Conteúdo com o fundo do court */}
    <div
      className="relative flex flex-1 flex-col items-center justify-center gap-7 px-6 text-center"
      style={{
        backgroundImage:
          'linear-gradient(135deg, rgba(0,0,0,0.45) 0%, rgba(0,95,70,0.85) 100%), url("https://res.cloudinary.com/dgedcigzi/image/upload/v1731360097/kcqb5hkrpychxmh0xlqe.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
    <div className="relative flex h-48 w-48 items-center justify-center">
      {/* brilho a pulsar */}
      <span className="absolute h-44 w-44 rounded-full bg-emerald-300/25 blur-3xl animate-pulse"></span>
      {/* anel a girar (spinner) */}
      <span
        className="absolute h-44 w-44 rounded-full border-[3px] border-white/20 border-t-white animate-spin"
        style={{ animationDuration: '1.2s' }}
      ></span>
      {/* logo da raquete — a "respirar" */}
      <img
        src={padelLogo}
        alt="PadelMaster"
        className="relative h-28 w-auto animate-pulse drop-shadow-2xl"
        style={{ animationDuration: '2.4s' }}
      />
    </div>
    <div>
      <h1 className="text-xl font-bold tracking-tight text-white drop-shadow sm:text-2xl">{title}</h1>
      <p className="mt-1.5 text-sm text-white/80">{subtitle}</p>
    </div>
    <div className="flex gap-1.5">
      <span className="h-2 w-2 animate-bounce rounded-full bg-white" style={{ animationDelay: '0ms' }}></span>
      <span className="h-2 w-2 animate-bounce rounded-full bg-white" style={{ animationDelay: '150ms' }}></span>
      <span className="h-2 w-2 animate-bounce rounded-full bg-white" style={{ animationDelay: '300ms' }}></span>
    </div>
    </div>
  </div>
);

interface ProtectedRouteProps {
  element: React.ReactElement;
  requireAuth?: boolean; // Por padrão, true
  showLoading?: boolean; // Por padrão, true
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  element, 
  requireAuth = true,
  showLoading = true 
}) => {
  const userProfile = useSelector((state: RootState) => state.userProfile.userProfile);
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Verificação inteligente — termina assim que possível (sem espera fixa):
    // • Sem token → decide já (vai para o login)
    // • Com token + dados já no Redux → entra já (instantâneo)
    // • Com token mas dados ainda a carregar → espera só o necessário (teto 4s)
    if (!isAuthenticated() || userProfile) {
      setIsLoading(false);
      setAuthChecked(true);
      return;
    }
    const safety = setTimeout(() => {
      setIsLoading(false);
      setAuthChecked(true);
      console.log('✅ [PROTECTED-ROUTE] Verificação concluída (teto de segurança)');
    }, 4000);
    return () => clearTimeout(safety);
  }, [userProfile]);

  // Se não precisa de autenticação, renderizar diretamente
  if (!requireAuth) {
    return element;
  }

  // Verificar se está autenticado
  const userIsAuthenticated = isAuthenticated();
  const hasUserData = !!userProfile;

  console.log('🔐 [PROTECTED-ROUTE] Estado atual:', {
    path: location.pathname,
    userIsAuthenticated,
    hasUserData,
    isLoading,
    authChecked
  });

  // Mostrar loading enquanto verifica autenticação
  if (isLoading && showLoading) {
    return (
      <VerifyingScreen
        title="Agradecemos a sua verificação"
        subtitle="A validar a tua sessão com segurança…"
      />
    );
  }

  // Se não está autenticado, redirecionar para login
  if (!userIsAuthenticated) {
    console.log('🚫 [PROTECTED-ROUTE] Usuário não autenticado - redirecionando para login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Se está autenticado mas não tem dados do usuário (ainda carregando dados)
  if (userIsAuthenticated && !hasUserData && !authChecked) {
    return (
      <VerifyingScreen
        title="A preparar a tua conta"
        subtitle="A restaurar a tua sessão, demora só uns segundos…"
      />
    );
  }

  // Se chegou até aqui, está autenticado e pode acessar a rota
  console.log('✅ [PROTECTED-ROUTE] Acesso permitido para:', location.pathname);
  return element;
};

export default ProtectedRoute;
